import { useEffect, useState, type JSX, type ReactNode } from 'react'
import { FormProvider, useForm, type DefaultValues, type UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { type z } from 'zod'
import { useToast } from '../system/ToastProvider.js'

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- z.ZodType requires three type params with no public default; any is the idiomatic way to express an open-ended Zod schema bound
interface ZodFormProps<S extends z.ZodType<any, any, any>> {
  schema: S
  defaultValues?: DefaultValues<z.input<S>>
  onSubmit: (data: z.output<S>) => Promise<void> | void
  children?: ReactNode
  render?: (methods: UseFormReturn<z.input<S>>) => ReactNode
  /**
   * If provided, the underlying <form> element is attached to this ref so
   * external callers (e.g. ResourceDrawer) can call formRef.current.requestSubmit().
   */
  formRef?: React.RefObject<HTMLFormElement | null>
  /**
   * Called whenever the form's dirty/valid/submitting state changes, so an
   * external Save button (e.g. a drawer footer) can reflect it without
   * needing its own submit control inside the form.
   */
  onFormStateChange?: (state: { isDirty: boolean; isValid: boolean; isSubmitting: boolean }) => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same open-ended Zod schema bound as ZodFormProps above
export function ZodForm<S extends z.ZodType<any, any, any>>(props: ZodFormProps<S>): JSX.Element {
  const [formError, setFormError] = useState<string | null>(null)
  const methods = useForm<z.input<S>>({
    resolver: zodResolver(props.schema) as never,
    ...(props.defaultValues !== undefined ? { defaultValues: props.defaultValues } : {}),
    // Real-time validation: errors appear/clear as the user types, not only
    // after they blur the field. reValidateMode defaults to 'onChange' so
    // errors also re-run on every keystroke after a failed submit.
    mode: 'onChange',
  })
  const toast = useToast()

  // onFormStateChange intentionally excluded from deps: parents pass an inline
  // callback, and including it would re-fire this effect (and the parent's
  // setState) every render.
  const { isDirty, isValid, isSubmitting } = methods.formState
  useEffect(() => {
    props.onFormStateChange?.({ isDirty, isValid, isSubmitting })
  }, [isDirty, isValid, isSubmitting])

  const submit = methods.handleSubmit(
    async (data) => {
      setFormError(null)
      try {
        await props.onSubmit(data as z.output<S>)
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        setFormError(msg)
        toast.error('Save failed', msg)
      }
    },
    (errors) => {
      const summaries = Object.values(errors)
        .map((err) =>
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message?: string }).message ?? '')
            : '',
        )
        .filter(Boolean)
      const summary =
        summaries.length > 0
          ? summaries.join('; ')
          : 'Please fix the highlighted fields'
      setFormError(summary)
      toast.error('Form has errors', summary)
    },
  )

  return (
    <FormProvider {...methods}>
      <form ref={props.formRef} onSubmit={submit} noValidate>
        {formError && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            {formError}
          </div>
        )}
        {props.children ?? props.render?.(methods)}
        {/* Hidden submit button so requestSubmit() works reliably in every browser */}
        <button type="submit" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
      </form>
    </FormProvider>
  )
}
